import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, CheckCircle2, Mail } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { value: "parent", label: "I'm a parent" },
  { value: "family", label: "Grandparent / family member" },
  { value: "retailer", label: "Retailer or distributor" },
  { value: "other", label: "Just curious" },
];

const inputCls =
  "w-full rounded-full border border-white/30 bg-white/95 px-6 py-4 text-sm font-semibold text-slate-900 outline-none transition-all duration-300 placeholder:font-normal placeholder:text-slate-400 focus:border-white focus:ring-4 focus:ring-white/25";

export const RegisterInterest = () => {
  const [form, setForm] = useState({ name: "", email: "", interest: "parent", message: "" });
  const [status, setStatus] = useState("idle");
  const [count, setCount] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/interest-count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/register-interest`, form);
      setStatus("done");
      setCount((c) => (typeof c === "number" ? c + 1 : c));
      toast.success("You're on the Mi Binky list — welcome!");
    } catch (err) {
      setStatus("idle");
      toast.error(err?.response?.data?.detail || "Something went wrong — please try again.");
    }
  };

  return (
    <section id="register" data-testid="register-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-binky-pink via-[#e5348e] to-[#c2247c] px-6 py-16 text-center text-white shadow-2xl shadow-binky-pink/40 sm:px-16 sm:py-20">
          <div className="pointer-events-none absolute -left-16 -top-16 h-64 w-64 rounded-full bg-white/15 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -right-16 h-72 w-72 rounded-full bg-binky-navy/30 blur-[90px]" />
          <Heart className="pointer-events-none absolute left-[8%] top-[18%] h-8 w-8 rotate-[-14deg] fill-white/20 text-white/20" />
          <Heart className="pointer-events-none absolute bottom-[16%] right-[9%] h-10 w-10 rotate-[12deg] fill-white/15 text-white/15" />

          <span className="inline-flex items-center gap-2 rounded-full border border-white/30 bg-white/15 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] backdrop-blur" data-testid="register-badge">
            Coming Soon
          </span>
          <h2 className="mx-auto mt-6 max-w-xl font-display text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl" data-testid="register-heading">
            Be first in line for Mi Binky
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-white/85 md:text-base">
            Register your interest for launch news, early pack availability, cotton refills and the
            15 ml Comfort Range — before anyone else.
          </p>
          {typeof count === "number" && count > 0 && (
            <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-white/70" data-testid="interest-count">
              Join {count} {count === 1 ? "person" : "people"} already on the list
            </p>
          )}

          {status === "done" ? (
            <div className="mx-auto mt-10 flex max-w-md flex-col items-center" data-testid="register-success-message">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/20">
                <CheckCircle2 className="h-8 w-8" />
              </span>
              <h3 className="mt-5 font-display text-2xl font-extrabold">You&apos;re on the list!</h3>
              <p className="mt-2 text-sm leading-relaxed text-white/85">
                Thank you for registering. We&apos;ll share launch news, refills and the Comfort
                Range with you first.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mx-auto mt-10 max-w-xl space-y-4" data-testid="register-interest-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <input
                  required
                  data-testid="register-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your name"
                  className={inputCls}
                />
                <input
                  required
                  type="email"
                  data-testid="register-email-input"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email address"
                  className={inputCls}
                />
              </div>
              <select
                data-testid="register-interest-select"
                value={form.interest}
                onChange={(e) => setForm({ ...form, interest: e.target.value })}
                className={inputCls}
              >
                {INTERESTS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="submit"
                data-testid="register-submit-button"
                disabled={status === "sending"}
                className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-binky-navy px-8 py-4 text-sm font-bold text-white shadow-xl shadow-binky-navy/40 transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60 sm:w-auto sm:px-12"
              >
                <Heart className="h-4 w-4 fill-binky-pink text-binky-pink transition-transform duration-300 group-hover:scale-125" />
                {status === "sending" ? "Registering…" : "Register Your Interest"}
              </button>
              <p className="text-[11px] leading-relaxed text-white/60">
                We&apos;ll only contact you about Mi Binky. No spam, ever.
              </p>
            </form>
          )}
        </Reveal>

        <Reveal delay={0.1} className="mx-auto mt-8 max-w-3xl">
          <div
            data-testid="contact-strip"
            className="flex flex-col items-center justify-between gap-5 rounded-[28px] border border-slate-100 bg-white px-8 py-7 shadow-[0_20px_50px_-24px_rgba(27,79,160,0.25)] sm:flex-row"
          >
            <div className="text-center sm:text-left">
              <h3 className="font-display text-xl font-bold text-slate-900">Questions about Mi Binky?</h3>
              <p className="mt-1 text-sm text-slate-600">
                Parents, retailers and distributors — we read every message.
              </p>
            </div>
            <a
              href="mailto:info@binky.life"
              data-testid="contact-email-button"
              className="inline-flex shrink-0 items-center gap-2 rounded-full bg-binky-blue px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-binky-blue/30 transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              <Mail className="h-4 w-4" />
              info@binky.life
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
